<template>
  <div class="job-worker-assignment">
    <div class="worker-assignment-search">
      <b-form-input
        v-model="search"
        placeholder="Search workers"
        debounce="200"
        size="sm"
      />
    </div>

    <div v-if="isLoading" class="worker-assignment-loading">
      <b-spinner small variant="primary" />
    </div>

    <template v-else>
      <div v-if="workers.length === 0" class="worker-assignment-empty">
        No workers available.
      </div>

      <template v-else>
        <div class="worker-assignment-toolbar">
          <small> {{ selectedIds.length }} selected </small>
          <div class="worker-assignment-actions">
            <b-button
              variant="link"
              size="sm"
              class="worker-assignment-link"
              @click="selectAll"
            >
              Select all
            </b-button>
            <b-button
              variant="link"
              size="sm"
              class="worker-assignment-link"
              @click="clearAll"
            >
              Clear
            </b-button>
          </div>
        </div>

        <div class="worker-list">
          <div
            v-if="filteredWorkers.length === 0"
            class="worker-assignment-empty"
          >
            No workers match "{{ search }}".
          </div>
          <b-form-checkbox
            v-for="worker in filteredWorkers"
            :key="worker.id"
            :checked="selectedIds.includes(worker.id)"
            class="worker-option"
            @change="toggle(worker.id, $event)"
          >
            <span class="worker-option-name">{{ worker.full_name }}</span>
            <span v-if="worker.trade" class="worker-option-trade">
              {{ formatTrade(worker.trade) }}
            </span>
          </b-form-checkbox>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import { formatTrade } from '@/utils/timesheetHelpers'

export default {
  name: 'JobWorkerAssignment',

  props: {
    // v-model — array of selected worker ids
    value: {
      type: Array,
      default: () => [],
    },
    projectId: {
      type: [Number, String],
      default: null,
    },
  },

  data() {
    return {
      workers: [],
      search: '',
      isLoading: false,
    }
  },

  computed: {
    selectedIds() {
      return Array.isArray(this.value) ? this.value : []
    },

    filteredWorkers() {
      const term = this.search.trim().toLowerCase()
      if (!term) return this.workers
      return this.workers.filter(
        (w) => w.full_name && w.full_name.toLowerCase().includes(term),
      )
    },
  },

  watch: {
    projectId() {
      this.loadWorkers()
    },
  },

  created() {
    this.loadWorkers()
  },

  methods: {
    formatTrade,

    async loadWorkers() {
      this.isLoading = true
      try {
        this.workers = await this.$store.dispatch(
          'schedule/getAvailableWorkers',
          { project_id: this.projectId || undefined },
        )
      } catch (err) {
        this.workers = []
      } finally {
        this.isLoading = false
      }
    },

    toggle(workerId, checked) {
      const next = this.selectedIds.filter((id) => id !== workerId)
      if (checked) next.push(workerId)
      this.$emit('input', next)
    },

    selectAll() {
      this.$emit(
        'input',
        this.workers.map((w) => w.id),
      )
    },

    clearAll() {
      this.$emit('input', [])
    },
  },
}
</script>

<style scoped>
.job-worker-assignment {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.worker-assignment-search /deep/ .form-control {
  height: 38px;
  border-color: #d1d5db;
  border-radius: 8px;
}

.worker-assignment-loading,
.worker-assignment-empty {
  padding: 1rem;
  color: #9ca3af;
  font-size: 0.82rem;
  text-align: center;
}

.worker-assignment-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  color: #6b7280;
  font-size: 0.78rem;
}

.worker-assignment-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.worker-assignment-link {
  padding: 0;
  font-size: 0.78rem;
  font-weight: 700;
}

.worker-list {
  max-height: 240px;
  overflow-y: auto;
  padding: 0.45rem;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #f9fafb;
}

.worker-option {
  display: block;
  margin: 0;
  min-height: 52px;
  padding: 0.55rem 0.65rem 0.55rem 2.35rem;
  border-radius: 8px;
  color: #111827;
}

.worker-option + .worker-option {
  margin-top: 0.15rem;
}

.worker-option:hover {
  background: #ffffff;
}

.worker-option /deep/ .custom-control-label {
  display: block;
  line-height: 1.25;
}

.worker-option /deep/ .custom-control-label::before,
.worker-option /deep/ .custom-control-label::after {
  top: 0.1rem;
  left: -1.8rem;
}

.worker-option-name {
  font-size: 0.82rem;
  font-weight: 700;
}

.worker-option-trade {
  display: block;
  margin-top: 0.1rem;
  color: #6b7280;
  font-size: 0.72rem;
}
</style>
